import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { QuestionDetail } from '@/features/question-library';
import { EditQuestionTemplate, useQuestionCreate } from '@/features/question-library';
import type { PostApiV1ExamQuestionLibraryItemMutationRequest } from '@/shared/api/generated';
import { useSession } from '@/shared/store/hooks/useSession';

export const Route = createFileRoute('/_authenticated/question-library/new/')({
  component: NewQuestionPage,
});

/** Questão padrão para o formulário de criação */
const EMPTY_QUESTION: QuestionDetail = {
  id: '',
  text: '',
  discipline: '',
  author: '',
  language: 'pt-BR',
  status: 'Pendente',
  isActive: true,
  type: '',
  isOwner: true,
  company: '',
  createdAt: '',
  weight: 1,
  level: null,
  showLabel: true,
  showInformation: false,
  teacherEvaluation: true,
  randomizeAlternatives: true,
  requiredField: true,
  alwaysInEvaluation: false,
  pageBreak: true,
  information: '',
  item: '',
  alternatives: [],
  updatedBy: '',
  updatedAt: '',
  authorId: null,
  disciplineId: null,
  companyId: null,
  componentType: 0,
  min: null,
  max: null,
  correctAnswer: null,
  decimalPlaces: null,
  maxAudioTimeInSeconds: null,
  maxFileSizeInMegabytes: null,
  acceptedFormats: [],
  subtype: null,
  modelAnswer: null,
  keyTerms: null,
  statusId: null,
  statusJustification: null,
};

function NewQuestionPage() {
  const navigate = useNavigate();
  const { session } = useSession();
  const { mutate: createQuestion, isPending: isSaving } = useQuestionCreate();

  function handleSave(data: Partial<QuestionDetail>) {
    if (!session?.personId) return;

    const alternatives = data.alternatives ?? [];
    const payload: PostApiV1ExamQuestionLibraryItemMutationRequest = {
      ComponentType: data.componentType ?? 1,
      CultureInfo: data.language ?? 'pt-BR',
      Label: data.text ?? '',
      PersonAuthorId: data.authorId ?? session.personId,
      ...(data.information ? { Information: data.information } : {}),
      ...(data.weight != null ? { Weight: data.weight } : {}),
      ...(data.level != null ? { Level: data.level } : {}),
      ...(data.showLabel !== undefined ? { ShowLabel: data.showLabel } : {}),
      ...(data.showInformation !== undefined ? { ShowInformation: data.showInformation } : {}),
      ...(data.pageBreak !== undefined ? { PageBreak: data.pageBreak } : {}),
      ...(data.randomizeAlternatives !== undefined
        ? { RandomizeAlternatives: data.randomizeAlternatives }
        : {}),
      ...(data.teacherEvaluation !== undefined
        ? { TeacherEvaluation: data.teacherEvaluation }
        : {}),
      ...(data.requiredField !== undefined ? { Required: data.requiredField } : {}),
      ...(data.alwaysInEvaluation !== undefined ? { Fixed: data.alwaysInEvaluation } : {}),
      ...(data.isActive !== undefined ? { Active: data.isActive } : {}),
      ...(data.disciplineId != null ? { DisciplineId: data.disciplineId } : {}),
      ...(data.companyId != null ? { CompanyId: data.companyId } : {}),
      ...(data.min != null ? { Min: data.min } : {}),
      ...(data.max != null ? { Max: data.max } : {}),
      ...(data.correctAnswer != null ? { CorrectAnswer: data.correctAnswer } : {}),
      ...(data.decimalPlaces != null ? { DecimalPlaces: data.decimalPlaces } : {}),
      ...(data.maxAudioTimeInSeconds != null
        ? { MaxAudioTimeInSeconds: data.maxAudioTimeInSeconds }
        : {}),
      ...(data.maxFileSizeInMegabytes != null
        ? { MaxFileSizeInMegabytes: data.maxFileSizeInMegabytes }
        : {}),
      // Subtipo dissertativo (MultilineText): o formulário só devolve valores
      // quando o subtipo selecionado os aceita.
      ...(data.subtype != null ? { Subtype: data.subtype } : {}),
      ...(data.modelAnswer ? { ModelAnswer: data.modelAnswer } : {}),
      ...(data.keyTerms ? { KeyTerms: data.keyTerms } : {}),
      ...(alternatives.length > 0
        ? {
            'ExamTypeList.ExamTypeListValues': alternatives.map(alt => ({
              value: alt.value,
              correctAnswer: alt.isCorrect,
            })),
          }
        : {}),
      ...(data.isAiAssisted ? { IsAiAssisted: true } : {}),
    };

    createQuestion({ data: payload });
  }

  return (
    <EditQuestionTemplate
      question={EMPTY_QUESTION}
      isLoading={false}
      isSaving={isSaving}
      mode="create"
      onSave={handleSave}
      onCancel={() => void navigate({ to: '/question-library' })}
    />
  );
}
